import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { IChat, IChatItem, IResponse } from '@interfaces/index';
import { HttpService } from '@ui-components/services';
import { CookieService } from 'ngx-cookie-service';
import { API_ENDPOINTS } from 'projects/ui-components/src/environments/api-endpoints';
import { Observable } from 'rxjs';
import { tryParseJSON } from '../utils/json.utils';

@Injectable({
  providedIn: 'root',
})
export class ChatService {

  constructor(
    protected _http_service: HttpService,
    private cookie_service: CookieService
  ) { }

  getOllieHelp(message_id: number): Observable<IResponse<string>> {
    return this._http_service.post('chat/get-ollie-help', { message_id });
  }

  /**
   * Streams the answer over server-sent events. Uses fetch rather than EventSource so the token
   * travels in the Authorization header: EventSource can't set headers, and a token in the URL
   * ends up in logs and monitoring.
   */
  getAnswerStream(message_id: number): Observable<{ text?: string; status?: string; message_id?: number }> {
    return new Observable((observer) => {
      const authToken = this.cookie_service.get('access_token');
      if (!authToken) {
        observer.error('🚨 No authentication token found.');
        return;
      }

      const url = `${environment.api}/api/${API_ENDPOINTS.CHAT.GET_ANSWER}?message_id=${message_id}`;
      const controller = new AbortController();

      const handleEvent = (event: string): boolean => {
        const data = event
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart())
          .join('\n');
        const parsed = tryParseJSON<{ text?: string; status?: string; message_id?: number }>(data);

        if (!parsed) {
          console.warn('⚠️ Skipping invalid SSE payload');
          return false;
        }

        if (parsed.text) {
          observer.next(parsed);
        } else {
          console.warn('⚠️ Skipping chunk without "text" field');
        }

        if (parsed.status === 'done') {
          observer.complete();
          return true;
        }
        return false;
      };

      (async () => {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${authToken}`, Accept: 'text/event-stream' },
          signal: controller.signal,
        });
        if (!response.ok || !response.body) {
          throw new Error(`Chat stream failed with status ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
          const events = buffer.split('\n\n');
          buffer = events.pop() ?? '';

          for (const event of events) {
            if (handleEvent(event)) {
              controller.abort();
              return;
            }
          }
        }
        // Stream ended without a 'done' status, same as EventSource closing early.
        throw new Error('Chat stream ended unexpectedly');
      })().catch((error) => {
        if (controller.signal.aborted) return;
        console.error('🚨 SSE error:', error);
        observer.error(error);
      });

      return () => controller.abort();
    });
  }

  startNewChat(): Observable<IResponse<IChat>> {
    return this._http_service.post('chat/start-new-chat', {})
  }

  sendQuestion(chat_id: number, question: string, coach_person_id: number | null, coachee_person_id: number | null): Observable<IResponse<IChatItem>> {
    return this._http_service.post('chat/send-question', {
      chat_id,
      coach_person_id,
      coachee_person_id,
      scenario: question
    })
  }
}